import { useState, useEffect, useCallback } from "react";
import { BellRing, BellOff, Loader2, ShieldCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";

/**
 * NotificationPreferencesCard — renders inside CustomerProfile so users can
 * manage push subscription state after onboarding:
 *   - Shows current permission state
 *   - Enable / Disable toggle
 *   - "Send test" button (only when active)
 */
export default function NotificationPreferencesCard() {
  const { requestPermission, subscribeToPush, unsubscribeFromPush, sendTestPush } = usePushNotifications();
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const [hasSubscription, setHasSubscription] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (typeof Notification !== "undefined") setPermission(Notification.permission);
    try {
      if (!("serviceWorker" in navigator)) {
        setHasSubscription(false);
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        setHasSubscription(false);
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      setHasSubscription(!!sub);
    } catch {
      setHasSubscription(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const granted = await requestPermission();
      setPermission(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
      if (!granted) {
        toast.error("Please allow notifications in your browser settings.");
        return;
      }
      const sub = await subscribeToPush();
      if (sub.success) {
        toast.success("Push notifications enabled.");
        setHasSubscription(true);
      } else {
        toast.error(`Could not enable (${sub.reason || "unknown"}).`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      const res = await unsubscribeFromPush();
      if (res.success) {
        toast.success("Push notifications disabled.");
        setHasSubscription(false);
      } else {
        toast.error("Could not disable push notifications.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const res = await sendTestPush();
      if (res?.sent > 0) {
        toast.success(`Test push sent to ${res.sent} device${res.sent > 1 ? "s" : ""}.`);
      } else {
        toast.warning("Subscribed but no test delivered. Try reloading and retry.");
      }
    } finally {
      setBusy(false);
    }
  };

  const unsupported = permission === "unsupported";
  const denied = permission === "denied";
  const active = permission === "granted" && hasSubscription;

  return (
    <div
      data-testid="notification-preferences-card"
      className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-base font-bold text-[#0A2540]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Push Notifications
          </h3>
          <p className="text-xs text-[#6B7280] mt-1 leading-relaxed">
            Get instant alerts for EMI reminders, application updates and payment confirmations.
          </p>
        </div>
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-emerald-50" : "bg-slate-100"}`}>
          {active ? (
            <BellRing className="h-5 w-5 text-emerald-600" />
          ) : (
            <BellOff className="h-5 w-5 text-slate-500" />
          )}
        </div>
      </div>

      {/* Status row */}
      <div className="flex items-center gap-2 text-xs mb-4">
        {unsupported && (
          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-semibold">
            Not supported in this browser
          </span>
        )}
        {!unsupported && active && (
          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full font-semibold">
            <ShieldCheck className="h-3 w-3" /> Active
          </span>
        )}
        {!unsupported && !active && denied && (
          <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2.5 py-1 rounded-full font-semibold">
            Blocked by browser
          </span>
        )}
        {!unsupported && !active && !denied && (
          <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-semibold">
            Not enabled
          </span>
        )}
      </div>

      {denied && !active && (
        <p className="text-xs text-[#475569] bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4">
          To re-enable, click the lock icon in your browser's address bar → Notifications → Allow, then reload this page.
        </p>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {!active && !denied && !unsupported && (
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy}
            data-testid="push-enable-profile-btn"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#1565C0] text-white text-sm font-semibold px-4 py-2 hover:bg-[#0D47A1] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Enable
          </button>
        )}
        {active && (
          <>
            <button
              type="button"
              onClick={handleTest}
              disabled={busy}
              data-testid="push-test-profile-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-[#1565C0] px-4 py-2 hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              <Send className="h-4 w-4" /> Send test
            </button>
            <button
              type="button"
              onClick={handleDisable}
              disabled={busy}
              data-testid="push-disable-profile-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white text-sm font-semibold text-red-600 px-4 py-2 hover:bg-red-50 transition-colors disabled:opacity-60"
            >
              <BellOff className="h-4 w-4" /> Disable
            </button>
          </>
        )}
      </div>
    </div>
  );
}
